import React, { useEffect, useState } from 'react';
import { PagePath } from '../../../navigation/navigation.enums';
import { Anchor, PageHeader, Pager, scoreLevelRAGColour, scorePercentageRAGColour, SearchBar, SkillLevel, SkillPercentage, Table, TableColumn, TableRow } from '@keplerco/core';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { PagerConnector } from '../../../components/general/pager-connector/pager-connector';
import { OrganizationLevelType, ScoreType } from '../../../enums';
import { ScoreComparison } from '../../../enums/score-comparison';
import { ScoresResponse, EntityScores } from '../../../models/overmind/analytics';
import { ScoresSearchParams, SearchingSearchParams } from '../../../models/overmind/search-params';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import { useAppActions, useAppState } from '../../../overmind';
import RankedEntitiesWidget from '../ranked-entities/ranked-entities.widget';
import { WidgetCard } from '../analytics-filters/widget-card';
import { SkeletonOverlay } from '../../../components/general/loading-state/skeleton-overlay';
import { extractHighestOrganizationLevel } from '../../../library/helpers/permissions/extract-highest-organization-level';
import { OrganizationLevel } from '../../../models/overmind/permissions';

export function RolesPage() {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables, user, permissions } = useAppState();
  const organizationLevelType: OrganizationLevelType = React.useMemo(
    () => (user?.isSystemAdmin ? OrganizationLevelType.Company : extractHighestOrganizationLevel(permissions?.analytics.organizationLevels)?.organizationLevel ?? OrganizationLevelType.Learner),
    [user?.isSystemAdmin, permissions?.analytics.organizationLevels]
  );
  const organizationLevel: OrganizationLevel | undefined = React.useMemo(() => permissions?.analytics.organizationLevels.find(organizationLevel => organizationLevel.organizationLevel === organizationLevelType), [permissions?.analytics.organizationLevels, organizationLevelType]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [roleScoresResponse, setRoleScoresResponse] = useState<ScoresResponse>();
  const [requestParams, setRequestParams] = useState<ScoresSearchParams & SearchingSearchParams>({
    sortAscending: true,
    page: 1,
    pageSize: 10,
    startDate: undefined,
    endDate: undefined,
    organizationLevel: organizationLevelType,
    companySlug: companyVariables.slug,
    departmentSlugs: undefined,
    teamSlugs: undefined,
    learnerSlug: undefined,
    search: undefined,
    scoreType: ScoreType.Role,
    scoreTypeSlugs: undefined,
    scoreComparison: ScoreComparison.RoleExpectedProficiency,
  });
  if (!!organizationLevel) {
    if (organizationLevelType === OrganizationLevelType.Department) requestParams.departmentSlugs = organizationLevel.entities.map(entity => entity.slug!);
    if (organizationLevelType === OrganizationLevelType.Team) requestParams.teamSlugs = organizationLevel.entities.map(entity => entity.slug!);
    if (organizationLevelType === OrganizationLevelType.Learner) requestParams.learnerSlug = user?.learnerSlug;
  }

  async function getData(requestParams: ScoresSearchParams & SearchingSearchParams) {
    const response = await actions.analyticsGetScoresByScoreType(requestParams);
    setRoleScoresResponse(response);
  }

  useEffect(() => {
    async function initialise() {
      setIsLoading(true);
      await getData(requestParams);
      setIsLoading(false);
    }

    initialise();
  }, [companyVariables.slug]);

  return (
    <div className="wrapper stack" style={{ gap: 30 }}>
      <PageHeader
        breadcrumbs={[
          { name: 'Analytics', url: PagePath.analyticsBase },
          { name: 'Roles', url: window.location.pathname },
        ]}
        title="Roles"
        divider
      />

      <SkeletonOverlay isLoading={isLoading} width="100%">
        <WidgetCard title="Company roles" showSkillLevelHelp>
          <SearchBar
            loading={isSearching}
            label="Search"
            onInput={async event => {
              setIsSearching(true);
              const search = (event.target as HTMLInputElement).value;
              setRequestParams({ ...requestParams, search, page: 1 });
              await getData({ ...requestParams, search, page: 1 });
              setIsSearching(false);
            }}
          />

          <Table
            emptyState={<EmptyState title="There are no role analytics yet" />}
            footerContent={
              <PagerConnector
                defaultPageNumber={requestParams.page}
                pageCount={roleScoresResponse?.totalPages ?? 0}
                onPageChange={async page => {
                  setIsLoading(true);
                  setRequestParams({ ...requestParams, page });
                  await getData({ ...requestParams, page });
                  setIsLoading(false);
                }}
              >
                {connector => <Pager {...connector} />}
              </PagerConnector>
            }
          >
            {roleScoresResponse?.results.map((entityScore: EntityScores) => {
              const overallScore = entityScore.score;
              const expectedScore = entityScore.comparisonScore;
              return (
                <TableRow id={entityScore.slug} key={entityScore.slug}>
                  <TableColumn id="roleName" label="Role Name">
                    <Anchor
                      hovertype="opacity"
                      textTransform="none"
                      onClick={event => {
                        event.stopPropagation();
                        keplerNavigate(`${PagePath.analyticsBase}${PagePath.analyticsRole.replace(':companySlug', companyVariables.slug!).replace(':roleSlug', entityScore.slug)}`);
                      }}
                    >
                      {entityScore.name}
                    </Anchor>
                  </TableColumn>
                  <TableColumn id="averageSkillScore" label="Average skill score">
                    {companyVariables.useLevels ? (
                      <SkillLevel
                        level={overallScore?.level ?? companyVariables.minLevel}
                        minLevel={companyVariables.minLevel}
                        maxLevel={companyVariables.maxLevel}
                        noLevel={!overallScore}
                        noLevelText="Not captured"
                        dotColour={scoreLevelRAGColour(overallScore?.level ?? companyVariables.minLevel, companyVariables.minLevel, companyVariables.maxLevel)}
                      />
                    ) : (
                      <SkillPercentage percentage={overallScore?.percentage ?? 0} noPercentageText="Not captured" noPercentage={!overallScore} barColour={scorePercentageRAGColour(overallScore?.percentage ?? 0)} />
                    )}
                  </TableColumn>
                  <TableColumn id="averageExpectedProficiency" label="Average expected proficiency">
                    {companyVariables.useLevels ? (
                      <SkillLevel
                        level={expectedScore?.level ?? companyVariables.minLevel}
                        minLevel={companyVariables.minLevel}
                        maxLevel={companyVariables.maxLevel}
                        noLevel={!expectedScore}
                        noLevelText="Not captured"
                        dotColour={scoreLevelRAGColour(expectedScore?.level ?? companyVariables.minLevel, companyVariables.minLevel, companyVariables.maxLevel)}
                      />
                    ) : (
                      <SkillPercentage percentage={expectedScore?.percentage ?? 0} noPercentageText="Not captured" noPercentage={!expectedScore} barColour={scorePercentageRAGColour(expectedScore?.percentage ?? 0)} />
                    )}
                  </TableColumn>
                </TableRow>
              );
            })}
          </Table>
        </WidgetCard>
      </SkeletonOverlay>

      <RankedEntitiesWidget title="Role skill scores" subtitle="An overview of role performance, highlighting roles that achieved the highest scores and those with opportunities for improvement." byScoreType organizationLevelType={organizationLevelType} entitySlugs={organizationLevel?.entities.map(entity => entity.slug!) ?? []} />
    </div>
  );
}
